import {getTestProps} from '@benchling/js-test-utils';
import {
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  createStyles,
} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import {
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import ModelHubLoadingState from './lib/ModelHubLoadingState';
import StatusBadge from './lib/StatusBadge';
import {usePredictionDetail, type PredictionDetail} from './lib/usePredictionDetail';
import {InputChips} from './prediction-jobs/InputChips';
import {
  PreviewSequenceNameCell,
  resolvePreviewSequenceItems,
  type PreviewSequenceDisplayItem,
} from './prediction-jobs/predictionInputSequences';

export interface PredictionDetailCrumb {
  label: string;
  // A crumb with an onClick renders as a link; the final (current) crumb omits it and renders as text.
  onClick?: () => void;
}

interface PredictionDetailPageProps {
  catalogByExternalReference: ScientificModelsByExternalReference;
  predictionId: string;
  // Navigation callbacks used to build the data-hierarchy breadcrumb trail; the trail is derived from
  // the prediction's own batch, identical regardless of how the detail was reached.
  onNavigateToBatches: () => void;
  onNavigateToBatch: (batchId: string) => void;
  onNavigateToPredictions: () => void;
}

export default function PredictionDetailPage({
  catalogByExternalReference,
  predictionId,
  onNavigateToBatches,
  onNavigateToBatch,
  onNavigateToPredictions,
}: PredictionDetailPageProps): JSX.Element {
  const {classes} = useStyles();
  const {prediction, error, refresh} = usePredictionDetail(predictionId);
  const predictionIdForAnalytics = prediction?.id;
  const trackedPredictionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (predictionIdForAnalytics != null && trackedPredictionIdRef.current !== predictionIdForAnalytics) {
      trackedPredictionIdRef.current = predictionIdForAnalytics;
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTION_DETAIL_VIEWED);
    }
  }, [predictionIdForAnalytics]);

  // The whole page — including breadcrumbs and title — waits for the prediction so we can show its
  // persisted name rather than an interim id. Until then, render a full-page error or loader.
  if (error != null && prediction == null) {
    return (
      <Stack
        className={classes.content}
        spacing="lg"
        {...getTestProps({component: 'PredictionDetailPage', element: 'root'})}
      >
        <Alert
          color="red"
          icon={<BenchlingIcon icon="exclamation-circle" />}
          title="Failed to load prediction"
        >
          <Group spacing="xs">
            <Text size="sm">{error}</Text>
            <Anchor component="button" onClick={refresh} size="sm" type="button">
              Retry
            </Anchor>
          </Group>
        </Alert>
      </Stack>
    );
  }

  if (prediction == null) {
    return (
      <Stack
        className={classes.content}
        spacing="lg"
        {...getTestProps({component: 'PredictionDetailPage', element: 'root'})}
      >
        <ModelHubLoadingState
          testProps={getTestProps({component: 'PredictionDetailPage', element: 'loader'})}
        />
      </Stack>
    );
  }

  // Data-hierarchy breadcrumb trail. Every crumb except the last is a link to that resource's canonical
  // URL; the last (current) crumb is the prediction's name as plain text. A prediction with a batch
  // shows Batches / {batchName} / {name}; one without falls back to Predictions / {name}.
  const hasBatch = prediction.batchName !== '';
  const breadcrumbs: Array<PredictionDetailCrumb> = hasBatch
    ? [
        {label: 'Batches', onClick: onNavigateToBatches},
        {
          label: prediction.batchName,
          onClick: () => {
            onNavigateToBatch(prediction.batchId);
          },
        },
        {label: prediction.name},
      ]
    : [{label: 'Predictions', onClick: onNavigateToPredictions}, {label: prediction.name}];
  const model = requireScientificModel(catalogByExternalReference, prediction.modelName);

  return (
    <Stack
      className={classes.content}
      spacing="lg"
      {...getTestProps({component: 'PredictionDetailPage', element: 'root'})}
    >
      <Stack spacing="md">
        <Breadcrumbs classNames={{separator: classes.breadcrumbSeparator}}>
          {breadcrumbs.map((crumb, index) =>
            crumb.onClick != null ? (
              <Anchor component="button" key={index} onClick={crumb.onClick} size="sm" type="button">
                {crumb.label}
              </Anchor>
            ) : (
              <Text color="dimmed" key={index} size="sm">
                {crumb.label}
              </Text>
            )
          )}
        </Breadcrumbs>

        <Group noWrap={true} position="apart">
          <Group spacing={12}>
            <Title className={classes.title} order={1}>
              {prediction.name}
            </Title>
            <StatusBadge status={prediction.status} />
          </Group>
          {prediction.status === 'SUCCEEDED' && (
            <HeaderDownloadActions onRefresh={refresh} prediction={prediction} />
          )}
        </Group>
      </Stack>

      <FactsCard model={model} prediction={prediction} />
      <StatusSection onRefresh={refresh} prediction={prediction} />
      <InputsSection model={model} prediction={prediction} />
    </Stack>
  );
}

function FactsCard({
  model,
  prediction,
}: {
  model: ScientificModelNode;
  prediction: PredictionDetail;
}): JSX.Element {
  const {classes} = useStyles();

  const configFilenameStem = prediction.batchName || prediction.id;
  const configFilenameSuffix = '.settings.json';
  const configFilename = `${configFilenameStem}${configFilenameSuffix}`;
  const downloadConfig = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTION_DETAIL_DOWNLOAD_SETTINGS_CLICKED);
    initiateTextDownload(JSON.stringify(prediction.configJson, null, 2), configFilename);
  };

  return (
    <Paper
      className={classes.factsCard}
      radius="md"
      withBorder={true}
      {...getTestProps({component: 'PredictionDetailPage', element: 'facts'})}
    >
      <Fact label="Model">
        <Text fw={600} size="sm">
          {model.displayName}
        </Text>
      </Fact>
      <Fact label="Inputs">
        <InputChips configJson={prediction.configJson} />
      </Fact>
      <Fact label="Batch">
        {prediction.batchName === '' ? (
          <Text color="dimmed" size="sm">
            —
          </Text>
        ) : (
          // Plain text: the {batchName} breadcrumb is now the single way to navigate to the batch.
          <Text className={classes.monoValue} size="sm">
            {prediction.batchName}
          </Text>
        )}
      </Fact>
      <Fact label="Added">
        <Text size="sm">{prediction.createdAt ? readableTime(prediction.createdAt) : '—'}</Text>
      </Fact>
      <Fact label="Last modified">
        <Text size="sm">{prediction.modifiedAt ? readableTime(prediction.modifiedAt) : '—'}</Text>
      </Fact>
      <Fact label="Batch settings">
        <Anchor
          aria-label={configFilename}
          className={classes.settingsLink}
          component="button"
          onClick={downloadConfig}
          size="sm"
          type="button"
        >
          <Tooltip label={configFilename} openDelay={500} withArrow={true}>
            <Group noWrap={true} spacing={6}>
              <BenchlingIcon aria-hidden={true} className={classes.settingsIcon} icon="download" />
              <Group className={classes.settingsFilename} noWrap={true} spacing={0}>
                <Text className={classes.settingsFilenameStem} component="span" size="sm" truncate={true}>
                  {configFilenameStem}
                </Text>
                <Text component="span" size="sm">
                  {configFilenameSuffix}
                </Text>
              </Group>
            </Group>
          </Tooltip>
        </Anchor>
      </Fact>
    </Paper>
  );
}

function StatusSection({
  prediction,
  onRefresh,
}: {
  prediction: PredictionDetail;
  onRefresh: () => void;
}): JSX.Element | null {
  const {classes} = useStyles();

  if (prediction.status === 'FAILED') {
    return (
      <Alert
        color="red"
        icon={<BenchlingIcon icon="exclamation-circle" />}
        title="This prediction failed"
        {...getTestProps({component: 'PredictionDetailPage', element: 'failed'})}
      >
        {/* A specific error message and Retry action are deferred until the backend exposes them. */}
        <Text size="sm">
          Something went wrong while running this prediction. Download the batch results for details.
        </Text>
      </Alert>
    );
  }

  if (prediction.status === 'RUNNING' || prediction.status === 'PENDING') {
    const isRunning = prediction.status === 'RUNNING';
    return (
      <Paper
        className={classes.statusCard}
        radius="md"
        withBorder={true}
        {...getTestProps({component: 'PredictionDetailPage', element: 'in-progress'})}
      >
        <Text fw={700} size="md">
          {isRunning ? 'Prediction is running' : 'Queued — waiting to start'}
        </Text>
        <Text align="center" className={classes.statusSubtext} color="dimmed" size="sm">
          {isRunning
            ? 'The predicted structure and metrics will appear here once the batch completes.'
            : 'This prediction is waiting for an available worker. Results will appear here when ready.'}
        </Text>
      </Paper>
    );
  }

  // Succeeded: render the predicted structure when a standardized (mmCIF) output is available.
  if (prediction.standardizedOutputDownloadUrl != null) {
    return <StructurePanel downloadUrl={prediction.standardizedOutputDownloadUrl} onRefresh={onRefresh} />;
  }
  return null;
}

function StructurePanel({downloadUrl, onRefresh}: {downloadUrl: string; onRefresh: () => void}): JSX.Element {
  const {classes} = useStyles();

  // Signed URLs expire (1h). If a stale URL is handed to Molstar it will fail to load, so refetch the
  // prediction (which mints a fresh URL) rather than rendering a broken canvas.
  if (isS3URLExpired(downloadUrl)) {
    onRefresh();
    return <LoadingFileScreen />;
  }

  return (
    <Stack spacing="xs" {...getTestProps({component: 'PredictionDetailPage', element: 'structure'})}>
      <Text className={classes.sectionLabel}>Structure prediction</Text>
      <div className={classes.viewerWrapper}>
        {/* Omit surfaceView so the viewer uses ViewerAutoPreset, which colors the structure by pLDDT
            confidence (from the mmCIF's ModelArchive QA annotation), matching the AlphaFold viewer. */}
        <MolstarViewer
          className="reset"
          format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER]}
          url={downloadUrl}
        />
      </div>
    </Stack>
  );
}

function HeaderDownloadActions({
  prediction,
  onRefresh,
}: {
  prediction: PredictionDetail;
  onRefresh: () => void;
}): JSX.Element {
  // Signed URLs expire; on a stale click, refetch the prediction to mint fresh URLs instead of opening
  // a dead link.
  const openUrl = (url: string | null): void => {
    if (url == null) {
      return;
    }
    if (isS3URLExpired(url)) {
      onRefresh();
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Group spacing="sm">
      {prediction.standardizedOutputDownloadUrl != null && (
        <Button
          leftIcon={<BenchlingIcon icon="download" />}
          onClick={() => {
            trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DOWNLOAD_STRUCTURE_PDB_OR_MMCIF_CLICKED);
            openUrl(prediction.standardizedOutputDownloadUrl);
          }}
          variant="secondary"
        >
          Download structure (.cif)
        </Button>
      )}
      {prediction.hasResultsFile && (
        <Button
          leftIcon={<BenchlingIcon icon="download" />}
          onClick={() => {
            trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DOWNLOAD_RESULTS_ZIP_CLICKED);
            openUrl(prediction.resultsDownloadUrl);
          }}
          variant="primary"
        >
          Download all results (.zip)
        </Button>
      )}
    </Group>
  );
}

function InputsSection({
  model,
  prediction,
}: {
  model: ScientificModelNode;
  prediction: PredictionDetail;
}): JSX.Element {
  const {classes} = useStyles();
  const [items, setItems] = useState<Array<PreviewSequenceDisplayItem>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    resolvePreviewSequenceItems(prediction.componentApiIds, model, prediction.configJson)
      .then((resolved) => {
        if (!cancelled) {
          setItems(resolved);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [model, prediction.componentApiIds, prediction.configJson]);

  return (
    <Stack spacing="xs">
      <Text className={classes.sectionLabel}>Inputs</Text>
      {isLoading ? (
        <ModelHubLoadingState
          size="sm"
          testProps={getTestProps({component: 'PredictionDetailPage', element: 'inputs-loader'})}
        />
      ) : items.length === 0 ? (
        <Text color="dimmed" size="sm">
          No components configured for this prediction.
        </Text>
      ) : (
        <Paper
          className={classes.tablePaper}
          radius="md"
          withBorder={true}
          {...getTestProps({component: 'PredictionDetailPage', element: 'inputs-table'})}
        >
          <Table className={classes.table} horizontalSpacing={14} verticalSpacing={10}>
            <thead>
              <tr>
                <th className={classes.componentColumn}>Component</th>
                <th>Sequence / SMILES</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td className={classes.componentColumn}>
                    <PreviewSequenceNameCell item={item} />
                  </td>
                  <td className={classes.sequenceCell}>{item.text}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function Fact({label, children}: {children: React.ReactNode; label: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.fact}>
      <div className={classes.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  breadcrumbSeparator: {
    color: theme.colors.gray[5],
  },
  componentColumn: {
    verticalAlign: 'top',
    width: '35%',
  },
  content: {
    minHeight: 'calc(100vh - 32px)',
  },
  fact: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
    minWidth: 0,
  },
  factLabel: {
    color: theme.colors.gray[6],
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  factsCard: {
    display: 'grid',
    gap: '18px 28px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    padding: '20px 22px',
  },
  monoValue: {
    fontFamily: theme.fontFamilyMonospace,
  },
  sectionLabel: {
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  settingsFilename: {
    flex: 1,
    minWidth: 0,
  },
  settingsFilenameStem: {
    flex: 1,
    minWidth: 0,
  },
  settingsIcon: {
    flexShrink: 0,
  },
  settingsLink: {
    maxWidth: '100%',
  },
  sequenceCell: {
    fontFamily: theme.fontFamilyMonospace,
    verticalAlign: 'top',
    wordBreak: 'break-all' as const,
  },
  statusCard: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    padding: '30px 24px',
  },
  statusSubtext: {
    maxWidth: 440,
  },
  table: {
    '& thead th': {
      backgroundColor: theme.colors.gray[0],
      borderBottom: `1px solid ${theme.colors.gray[3]}`,
      color: theme.colors.gray[6],
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: '0.03em',
      textAlign: 'left' as const,
      textTransform: 'uppercase' as const,
    },

    '& td, & th': {
      verticalAlign: 'middle',
    },
  },
  tablePaper: {
    overflow: 'auto',
  },
  title: {
    fontFamily: theme.fontFamilyMonospace,
  },
  viewerWrapper: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    boxSizing: 'border-box' as const,
    height: 'min(560px, 65vh)',
    minHeight: 320,
    overflow: 'hidden',
    width: '100%',
  },
}));
